/*
 * restock-schedule.js — "Back Thu" for sold-out scheduled-restock SKUs (B2C-317)
 *
 * The server renders the whole state: snippets/pcard-restock-button.liquid outputs the
 * amber control with fallback labels that are true without a clock ("Mon & Thu" on
 * cards, "Back Mon & Thu" on the PDP). This module only narrows them to the first
 * delivery day strictly after today, Dubai time ("Back Thu", "Back on Thursday"). The
 * SKU list and the days live in snippets/restock-detect.liquid; there is no JS copy.
 * Spec: decisions/spec-b2c-316-restock-schedule.md (§Runtime).
 *
 * - Fail open: a control with no valid day code, or missing a template or a day name
 *   this pass needs, keeps its server fallback. Nothing throws.
 * - Compare before every write: setting textContent fires a childList mutation even
 *   when the text is unchanged, and the controls sit inside observed containers, so an
 *   unconditional write would re-trigger the observer every frame.
 * - Never touches the PDP banner (static), emits and listens to no events.
 * - Own fixed UTC+4 clock (kitchen-closed decision Q2, option b): kitchen-config.js and
 *   delivery-engine.js are not read.
 *
 * QA override: ?restock_at=YYYY-MM-DD treats that date as today in Dubai. Read once at
 * load, so a later URL rewrite (infinite-scroll page param, filter state) cannot move
 * the day mid-session. Invalid values are ignored. No midnight timer while it is set.
 */
(function () {
  'use strict';

  var DUBAI_OFFSET_MS = 4 * 60 * 60 * 1000; // fixed UTC+4, no DST
  var DAY_MS = 24 * 60 * 60 * 1000;
  var MIDNIGHT_CUSHION_MS = 1000;
  // Index = Date#getUTCDay() (0 = Sunday). Same codes as restock-detect.
  var CODES = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  var DAY = '__DAY__';
  var DAY_SHORT = '__DAY_SHORT__';

  /* ---- Clock ----------------------------------------------------- */

  // A Date whose UTC fields read as the Dubai wall clock, whatever the device timezone.
  function dubaiNow() {
    return new Date(Date.now() + DUBAI_OFFSET_MS);
  }

  // ?restock_at=YYYY-MM-DD -> the same shape as dubaiNow() (noon that day), else null.
  function readOverride() {
    var m = window.location.search.match(/[?&]restock_at=(\d{4})-(\d{2})-(\d{2})(?:&|$)/);
    if (!m) { return null; }
    var y = +m[1];
    var mo = +m[2] - 1;
    var d = +m[3];
    var date = new Date(Date.UTC(y, mo, d, 12));
    // Rejects dates that roll over (2026-02-30 -> 2 March).
    if (date.getUTCFullYear() !== y || date.getUTCMonth() !== mo || date.getUTCDate() !== d) {
      return null;
    }
    return date;
  }

  var override = readOverride();

  function today() {
    return override || dubaiNow();
  }

  /* ---- Pure core ------------------------------------------------- */

  // (['mon', 'thu'], dubaiDate) -> the code of the first listed weekday strictly after
  // the date's weekday, or null when the list holds no valid code.
  function nextDeliveryDay(days, now) {
    var todayIndex = now.getUTCDay();
    for (var offset = 1; offset <= 7; offset++) {
      var code = CODES[(todayIndex + offset) % 7];
      if (days.indexOf(code) !== -1) { return code; }
    }
    return null;
  }

  function parseDays(value) {
    var out = [];
    var parts = (value || '').split(',');
    for (var i = 0; i < parts.length; i++) {
      var code = parts[i].trim().toLowerCase();
      if (CODES.indexOf(code) !== -1 && out.indexOf(code) === -1) { out.push(code); }
    }
    return out;
  }

  /* ---- DOM pass (idempotent, no-op writes skipped) --------------- */

  function fill(template, placeholder, value) {
    return template.split(placeholder).join(value);
  }

  function setText(el, text) {
    if (el.textContent !== text) { el.textContent = text; }
  }

  function applyOne(control, now) {
    var code = nextDeliveryDay(parseDays(control.getAttribute('data-restock-days')), now);
    if (!code) { return; }

    var name = control.getAttribute('data-restock-day-' + code);
    var shortName = control.getAttribute('data-restock-day-short-' + code);
    var labelTpl = control.getAttribute('data-restock-label');
    var shortTpl = control.getAttribute('data-restock-label-short');
    var ariaTpl = control.getAttribute('data-restock-aria');
    var longEl = control.querySelector('.c-pcard__closed-label');
    var shortEl = control.querySelector('.c-pcard__closed-label--short');
    if (!name || !shortName || !labelTpl || !shortTpl || !ariaTpl || !longEl || !shortEl) {
      return; // fail open: the server fallback stays
    }

    // Fill all three first, then write them in one synchronous pass, so the visible
    // text and the accessible name never disagree.
    var label = fill(labelTpl, DAY, name);
    var shortLabel = fill(shortTpl, DAY_SHORT, shortName);
    var aria = fill(ariaTpl, DAY, name);

    setText(longEl, label);
    setText(shortEl, shortLabel);
    if (control.getAttribute('aria-label') !== aria) { control.setAttribute('aria-label', aria); }
  }

  function applyAll() {
    var controls = document.querySelectorAll('[data-restock-days]');
    if (!controls.length) { return; }
    var now = today();
    for (var i = 0; i < controls.length; i++) {
      applyOne(controls[i], now);
    }
  }

  /* ---- Dubai midnight + visibility backstop ---------------------- */

  var midnightTimer = null;

  function msToNextMidnight() {
    var d = dubaiNow();
    var sinceMidnight = d.getUTCHours() * 3600000 + d.getUTCMinutes() * 60000 +
      d.getUTCSeconds() * 1000 + d.getUTCMilliseconds();
    return DAY_MS - sinceMidnight + MIDNIGHT_CUSHION_MS;
  }

  function armMidnight() {
    if (override) { return; } // a pinned date never changes
    if (midnightTimer) { clearTimeout(midnightTimer); }
    midnightTimer = setTimeout(function () {
      midnightTimer = null;
      applyAll();
      armMidnight();
    }, msToNextMidnight());
  }

  document.addEventListener('visibilitychange', function () {
    if (document.hidden) { return; }
    applyAll();
    armMidnight(); // a sleeping device can deliver the timer late; re-aim it
  });

  /* ---- Late cards ------------------------------------------------- */

  // Same targets as kitchen.js: predictive search and every [data-kitchen-observe]
  // container insert cards after load. Every target is a permanent element rendered on
  // page load, so the observer lives as long as the page and is never disconnected
  // (CONSTITUTION §VI.21). A container that later gains the hook needs no change here.
  function observeLateCards() {
    if (!('MutationObserver' in window)) { return; }
    var targets = document.querySelectorAll('predictive-search-component, [data-kitchen-observe]');
    if (!targets.length) { return; }
    var pending = false;
    var mo = new MutationObserver(function () {
      if (pending) { return; }
      pending = true;
      requestAnimationFrame(function () { pending = false; applyAll(); });
    });
    for (var i = 0; i < targets.length; i++) {
      mo.observe(targets[i], { childList: true, subtree: true });
    }
  }

  /* ---- Init ------------------------------------------------------- */

  function init() {
    applyAll();
    armMidnight();
    observeLateCards();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
